import { defineStore } from 'pinia'
import { api } from '@/lib/api'
import type { AppNotification } from '@/lib/types'

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    items: [] as AppNotification[],
    loading: false,
  }),

  getters: {
    unread(state): AppNotification[] {
      return state.items.filter((n) => !n.read_at)
    },
    unreadCount(): number {
      return this.unread.length
    },
    /** Pursuit ids with unread action-type notifications (Today "Action Needed"). */
    unreadActionPursuitIds(): Set<string> {
      const ids = new Set<string>()
      for (const n of this.unread) {
        if (n.pursuit_id && n.kind.includes('action')) ids.add(n.pursuit_id)
      }
      return ids
    },
  },

  actions: {
    async fetch() {
      this.loading = true
      try {
        const res = await api.listNotifications()
        this.items = res.notifications ?? []
      } catch {
        // Bell shows stale data on failure; next SSE/poll refresh retries.
      } finally {
        this.loading = false
      }
    },

    async markRead(id: string) {
      const item = this.items.find((n) => n.id === id)
      if (item && !item.read_at) item.read_at = new Date().toISOString()
      try {
        await api.markNotificationRead(id)
      } catch {
        if (item) item.read_at = null
      }
    },

    async markAllRead() {
      const previous = this.items.map((n) => n.read_at)
      const now = new Date().toISOString()
      for (const n of this.items) if (!n.read_at) n.read_at = now
      try {
        await api.markNotificationRead('all')
      } catch {
        this.items.forEach((n, i) => (n.read_at = previous[i]))
      }
    },

    reset() {
      this.items = []
    },
  },
})
